import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
} from '@chakra-ui/react'
import mapboxgl from 'mapbox-gl'
import Head from 'next/head'
import { useEffect, useRef, useState } from 'react'
import slugify from 'slugify'

export type MapItem = {
  lng: string
  lat: string
  title: string
  slug: string
  image: string
}

type MapProps = {
  items: MapItem[]
  sectionUrl: string
  itemType: string
}

const setPopup = (item: MapItem, sectionUrl: string, itemType: string) =>
  new mapboxgl.Popup().setHTML(`
          <div class="mapThumbnail">
            <img class="mapThumbnailImage" alt="${item.title}" title="${item.title}" src="${item.image}"/>
            <div class="mapThumbnailHeader">
              <h2 class="mapThumbnailTitle">${item.title}</h2>
              <a class="mapThumbnailLink" href="${sectionUrl}${item.slug}">View&nbsp;${itemType}</a>
            </div>
          </div>
        `)

const removePopups = () => {
  const popUps = document.querySelectorAll('.mapboxgl-popup')

  /** Check if there is already a popup on the map and if so, remove it */
  if (popUps[0]) popUps[0].remove()
}

const jumpMenu = [
  {
    options: {
      center: [-95, 39],
      zoom: 3.2,
      essential: true,
    },
    label: 'USA',
  },
  {
    options: {
      center: [-95, 50],
      zoom: 3.2,
      essential: true,
    },
    label: 'Canada',
  },
  {
    options: {
      center: [5, 50],
      zoom: 3.2,
      essential: true,
    },
    label: 'Europe',
  },
  {
    options: {
      center: [150, -30],
      zoom: 2,
      essential: true,
    },
    label: 'Oceania',
  },
]

export const Map = (props: MapProps): JSX.Element | null => {
  const map = useRef<any>(null)

  const [isFullScreen, setIsFullScreen] = useState(false)
  const [currentItem, setCurrentItem] = useState<MapItem | null>()

  // Load map
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_MAPBOX_TOKEN) {
      throw new Error('Missing Mapbox token')
    }

    map.current = new mapboxgl.Map({
      container: 'map-graphic',
      style: 'mapbox://styles/debossgarage/ckua2xf5y507s18odyh5a3n0s',
      zoom: 3.75,
      center: [-85, 40],
      attributionControl: false,
      scrollZoom: false,
      accessToken: process.env.NEXT_PUBLIC_MAPBOX_TOKEN,
    })

    // Wait for map to load
    map.current.on('load', () => {
      map.current.resize()
      map.current.flyTo({
        center: [-85, 40],
        zoom: 3.75,
      })
    })

    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showUserLocation: true,
      })
    )

    // If there is a mouse, add the mouse controls
    if (window.matchMedia('(pointer:fine)').matches) {
      const nav = new mapboxgl.NavigationControl()
      map.current && map.current.addControl(nav, 'bottom-right')
    }

    props.items.forEach(item => {
      const marker = new mapboxgl.Marker({
        color: '#C10D38',
      })
        .setLngLat([Number(item.lng), Number(item.lat)])
        .setPopup(
          setPopup(item, props.sectionUrl, props.itemType)
            .on('open', () => {
              setCurrentItem(item)
            })
            .on('close', () => {
              setCurrentItem(null)
            })
        )
      marker.addTo(map.current)
    })
  }, [props.items, props.itemType, props.sectionUrl, map])

  // Scroll to current item when it changes
  useEffect(() => {
    if (currentItem) {
      const element = document?.querySelector(
        `#map-item-${slugify(currentItem.slug)}`
      ) as HTMLElement
      if (element) element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [currentItem])

  // Exit full screen when escape key is pressed
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullScreen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  // Resize the map when full screen is toggled
  useEffect(() => {
    map.current.resize()
  }, [isFullScreen])

  return (
    <div id='map'>
      <Head>
        <link
          href='https://api.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css'
          rel='stylesheet'
        />
        <style>
          {`
            .mapThumbnail {
              overflow: hidden;
              position: relative;
              font-family: "Bebas Neue",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
            }
            .mapThumbnailImage {
              object-fit: cover;
              object-position: center;
              width: 236px;
              height: 150px;
            }
            .mapThumbnailHeader {
              padding: .5rem;
              display: flex;
              gap: .5rem;
              justify-content: space-between;
            }
            .mapThumbnailTitle {
              font-size: 1.5rem;
              font-weight: 700;
            }
            .mapThumbnailLink {
              font-size: .875rem;
              position: relative;
            }
            .mapboxgl-ctrl-logo {
              bottom: 2px !important;
              left: 2px !important;
              transform: scale(0.5);
              transform-origin: bottom left;
            }
            .mapboxgl-popup {
              z-index: 101;
            }
          `}
        </style>
      </Head>
      <Grid
        backgroundColor='gray.150'
        bottom={0}
        flexWrap='wrap'
        gridTemplateRows='124px 0 1fr'
        h={isFullScreen ? '100vh' : '72vh'}
        left={0}
        maxW={isFullScreen ? '100%' : '6xl'}
        mx='auto'
        overflow='hidden'
        pos={isFullScreen ? 'fixed' : 'relative'}
        pt={isFullScreen ? 0 : 4}
        px={isFullScreen ? 0 : 4}
        right={0}
        top={0}
        zIndex={isFullScreen ? 100 : 1}>
        <Flex overflowX='auto' cursor='grab' backgroundColor='gray.100'>
          {props.items.map(item => (
            <button
              id={`map-item-${item.slug}`}
              onClick={() => {
                map.current.flyTo({
                  center: [Number(item.lng), Number(item.lat)],
                  zoom: 10,
                  essential: true,
                })
                removePopups()
                setPopup(item, props.sectionUrl, props.itemType)
                  .setLngLat([Number(item.lng), Number(item.lat)])
                  .setOffset([0, -30])
                  .on('close', () => {
                    setCurrentItem(null)
                  })
                  .addTo(map.current)
                setCurrentItem(item)
              }}
              key={item.slug}>
              <Box
                backgroundColor={
                  currentItem?.slug === item.slug ? 'gray.200' : 'gray.100'
                }
                borderColor={
                  currentItem?.slug === item.slug ? 'gray.300' : 'gray.100'
                }
                borderRadius='4px'
                borderWidth='2px'
                width='100px'
                height='100px'
                overflow='hidden'
                p={1}>
                <Stack>
                  <AspectRatio ratio={12 / 9} backgroundColor='gray.300'>
                    <img
                      alt={item.title}
                      height={75}
                      loading='lazy'
                      src={item.image}
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                      width={100}
                    />
                  </AspectRatio>
                  <Heading
                    fontSize='md'
                    textAlign='left'
                    lineHeight='1'
                    className='truncate'>
                    {item.title}
                  </Heading>
                </Stack>
              </Box>
            </button>
          ))}
        </Flex>
        <Box
          position='relative'
          zIndex={100}
          maxWidth='calc(100% - 50px)'
          top='10px'>
          <Box position='absolute' right={0}>
            <Menu>
              <MenuButton
                boxShadow='xs'
                borderRadius='4px'
                height='29px'
                backgroundColor='white'
                fontSize='2xs'
                padding={2}
                fontWeight='bold'
                _hover={{
                  backgroundColor: 'gray.100',
                  boxShadow: 'md',
                }}>
                JUMP&nbsp;TO
              </MenuButton>
              <MenuList maxWidth={20}>
                {jumpMenu.map(item => (
                  <MenuItem
                    onClick={() => {
                      map.current.flyTo({ ...item.options })
                    }}
                    key={item.label}
                    fontWeight='bold'
                    fontSize='sm'>
                    {item.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Box>
          <Box position='absolute' left='8px'>
            <Button
              boxShadow='xs'
              borderRadius='4px'
              height='29px'
              onClick={() => setIsFullScreen(!isFullScreen)}
              fontSize='2xs'
              fontWeight='bold'
              padding={2}
              _hover={{
                backgroundColor: 'gray.100',
                boxShadow: 'md',
              }}>
              {isFullScreen && 'Exit'} Full Screen
            </Button>
          </Box>
        </Box>
        <Box id='map-graphic' bgColor='gray.300' />
      </Grid>
    </div>
  )
}
